import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { clsx } from 'clsx';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface QuestionSelectorOption {
    value: string | number | null;
    label: string;
    /** True for questions the user hasn't reached yet — renders dim + unclickable. */
    disabled?: boolean;
}

export interface QuestionSelectorProps {
    value: string | number | null;
    onChange: (value: string | number | null) => void;
    options: QuestionSelectorOption[];
    /** Fallback label when no option matches `value`. */
    placeholder?: string;
    /** Optional wrapper class — `relative` is added so the absolute panel anchors correctly. */
    className?: string;
    /** Width of the dropdown panel; falls back to trigger width when omitted. */
    dropdownWidth?: number | string;
}

// Tertiary-button-styled question selector for the PageHeader of psychological questionnaires
// (ML360, SelfDetermination, PersonalCoping, QuestionnaireQuestionStep). Per Figma 19792:86646
// (trigger) + 19792:86970 (panel) — trigger is text + chevron in blue, no bg/border; panel
// shows three item states: answered (neutral text + filled teal check badge), current
// (`bg-teal-50` light / `bg-neutral-100` dark + teal-600 text + simple check), future
// (`text-neutral-300` dim, disabled).
//
// Bespoke Listbox usage (instead of composing the Dropdown primitive) preserves explicit
// fullscreen-portal handling: when `document.fullscreenElement` is set, the panel renders
// `position: fixed` inside the fullscreen subtree rather than portaling to `document.body`
// (Headless UI's default), which would otherwise be invisible while a launcher is in the
// browser's fullscreen mode.
const QuestionSelector = ({
    value,
    onChange,
    options,
    placeholder,
    className,
    dropdownWidth,
}: QuestionSelectorProps) => {
    const [fullscreenElement, setFullscreenElement] = useState<Element | null>(null);
    const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setFullscreenElement(document.fullscreenElement);
            if (buttonRef.current) {
                setButtonRect(buttonRef.current.getBoundingClientRect());
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        setFullscreenElement(document.fullscreenElement);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const selectedOption = useMemo(() => options.find(o => o.value === value), [options, value]);
    const triggerLabel = selectedOption?.label ?? placeholder ?? '';

    const dropdownStyle = useMemo(() => {
        if (!buttonRect) {
            return undefined;
        }
        const width = dropdownWidth ?? buttonRect.width;
        const baseStyle = { width: typeof width === 'number' ? `${width}px` : width };
        if (fullscreenElement) {
            return {
                ...baseStyle,
                left: `${buttonRect.left}px`,
                top: `${buttonRect.bottom + 8}px`,
            };
        }
        return baseStyle;
    }, [buttonRect, fullscreenElement, dropdownWidth]);

    const updateButtonRect = () => {
        if (buttonRef.current) {
            setButtonRect(buttonRef.current.getBoundingClientRect());
        }
    };

    return (
        <Listbox value={value} onChange={onChange}>
            {({ open }) => (
                <div className={clsx('relative', className)}>
                    <ListboxButton
                        ref={buttonRef}
                        className="inline-flex items-center justify-start gap-1.5 rounded-md py-2 text-blue-600 hover:text-blue-700"
                        onClick={updateButtonRect}
                    >
                        <span className="text-base leading-6 font-semibold">{triggerLabel}</span>
                        <ChevronDown
                            aria-hidden="true"
                            className={clsx('size-4 transition-transform', open && 'rotate-180')}
                        />
                    </ListboxButton>

                    <ListboxOptions
                        anchor={fullscreenElement ? undefined : 'bottom start'}
                        className={clsx(
                            'min-w-60',
                            fullscreenElement && buttonRect
                                ? 'fixed z-[9999] max-h-80 overflow-auto'
                                : 'absolute z-9999 mt-1 max-h-56 overflow-auto',
                            'bg-popover rounded-md p-[5px] text-neutral-900',
                            'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-neutral-200',
                            'data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in data-[closed]:data-[leave]:opacity-0'
                        )}
                        style={dropdownStyle}
                        transition
                    >
                        {options.map(option => {
                            const isSelected = option.value === value;
                            const isDisabled = option.disabled ?? false;
                            return (
                                <ListboxOption
                                    key={String(option.value)}
                                    value={option.value}
                                    disabled={isDisabled}
                                    className={clsx(
                                        'inline-flex w-full cursor-pointer items-center justify-between rounded-md px-2 py-1.5 select-none',
                                        isSelected
                                            ? 'bg-teal-50 text-teal-600'
                                            : isDisabled
                                              ? 'cursor-not-allowed text-neutral-300'
                                              : 'text-neutral-900 data-[focus]:bg-neutral-100'
                                    )}
                                >
                                    <span className="pr-4 text-base leading-6 font-medium">{option.label}</span>
                                    {!isDisabled &&
                                        (isSelected ? (
                                            <Check className="size-4 shrink-0 text-teal-600" strokeWidth={1.5} />
                                        ) : (
                                            <div className="relative grid size-4 shrink-0 place-items-center rounded-full bg-teal-600">
                                                <Check className="size-3 text-white" strokeWidth={2.5} />
                                            </div>
                                        ))}
                                </ListboxOption>
                            );
                        })}
                    </ListboxOptions>
                </div>
            )}
        </Listbox>
    );
};

export default QuestionSelector;
